import React, {
	useRef,
	useState,
	useEffect,
	useCallback,
	useContext
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, useRequestData } from 'wsm-common-data';
import { trackEvent } from 'wsm-specials-display';
import {
	isProgramAspectRatioAccount,
	getCarouselDisplayProps
} from '../../utilities/aspect';
import { aR as ASPECT_RATIO } from '../../global-constants';
import 'slick-carousel/slick/slick.scss';
import Promo from '../Promo/Promo';
import CarouselControls from './CarouselControls';
import { setListingsCarouselSlidesCondition } from '../../actions/generalState';
import { shouldShowDetailsButton } from '../../utilities/general';
import { PrefsContext } from '../../contexts';

const Carousel = ({
	isMobile = false,
	promos,
	width,
	isCouponCaro = false
}) => {
	const {
		aspectRatio,
		enableAppleWallet,
		enablePrint,
		autoplay,
		autoplaySpeed
	} = useContext(PrefsContext);
	const sliderRef = useRef();
	const dispatch = useDispatch();
	// The first slide is always viewed
	const [viewedSlides, setViewedSlides] = useState([0]);
	const labels = useLabels();
	const numSlides = promos.length;
	const { widgetName, windowId } = useRequestData();
	const [visibleSlideIndex, setVisibleSlideIndex] = useState(0);
	const [isSlideVisible, setIsSlideVisible] = useState(false);
	const [promoOutsiderButton, setPromoOutsiderButton] = useState(null);
	const modalStateFromModal = useSelector((state) => state.modal.isModalOpen);
	const account = useSelector((state) => state.account);
	const isSlideshow =
		aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_SLIDESHOW;
	const programAspectRatioAccount = isProgramAspectRatioAccount(account);
	const isAdaptiveHeightEnabled =
		isSlideshow &&
		(programAspectRatioAccount === 'bmw' ||
			programAspectRatioAccount === 'mini');
	const isTallVerticalCoupon =
		aspectRatio === ASPECT_RATIO.DESKTOP_9X16 ||
		aspectRatio === ASPECT_RATIO.MOBILE_9X16;

	// Have customAssets and shouldShowDetailsButton = true => push down the control carousel button
	const isShowOfferDetailsCustomAsset =
		aspectRatio === ASPECT_RATIO.DESKTOP_10X1
			? promos.some(
					(promo) =>
						promo?.customAssets &&
						promo?.customAssets[ASPECT_RATIO.DESKTOP_10X1] &&
						shouldShowDetailsButton(
							promo,
							promo.incentiveData,
							aspectRatio,
							enableAppleWallet,
							enablePrint
						)
			  )
			: false;

	// Set up autoplay state
	const [autoplaySlides, setAutoplaySlides] = useState(false);
	// State to ignore infinitely wrap around contents to fix slide arrow make the special blank
	const [infiniteFlag, setInfiniteFlag] = useState(true);

	useEffect(() => {
		// Toggle carousel play/pause using our state
		if (autoplaySlides) {
			sliderRef.current.slickPlay();
		} else {
			sliderRef.current.slickPause();
		}
	}, [autoplaySlides]);

	useEffect(() => {
		// Set initial autoplay state based on preferences
		setAutoplaySlides(autoplay === 'true');
	}, [autoplay]);

	useEffect(() => {
		// Toggle carousel play/pause using our state
		if (typeof modalStateFromModal === 'boolean')
			setAutoplaySlides(!modalStateFromModal);
	}, [modalStateFromModal]);

	useEffect(() => {
		document?.addEventListener('keydown', (keyCode) => {
			if (keyCode.key === 'Tab') {
				setInfiniteFlag(false);
			} else {
				setInfiniteFlag(true);
			}
		});

		if (isCouponCaro && promos.length > 2) {
			dispatch(
				setListingsCarouselSlidesCondition({
					isCouponCaroFullSlide: true
				})
			);
		}
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [isCouponCaro, promos.length]);

	// Override Slick's default values with our own
	// See https://react-slick.neostack.com/docs/api/ for values
	const slickDefaults = {
		dots: true,
		infinite: false,
		dotsClass: setClassNames([
			isCouponCaro ? 'mt-3' : 'absolute-bottom',
			!isMobile ? 'on-desktop' : '',
			isCouponCaro ? 'coupon-carousel-controls' : '',
			'carousel-indicator-list',
			'dots',
			'infinite-dots',
			'w-100',
			'd-flex',
			'justify-content-center',
			isShowOfferDetailsCustomAsset ? 'isShowOfferDetailsCustomAsset' : ''
		]),
		arrows: !isMobile,
		adaptiveHeight: isAdaptiveHeightEnabled
	};

	// for 4x3 coupon carousel
	const couponCaroProps = {
		slidesToShow: 3,
		slidesToScroll: 3,
		responsive: [
			{
				breakpoint: 992,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2
				}
			}
		]
	};

	// for tall vertical carousel
	const tallVerticalCouponCaroProps = {
		slidesToShow: 4,
		slidesToScroll: 4,
		responsive: [
			{
				breakpoint: 992,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 3
				}
			},
			{
				breakpoint: 764,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2
				}
			}
		]
	};

	const getCarouselProps = () => {
		if (!isCouponCaro) return {};

		// For mobile, always show 1 slide regardless of carousel type
		if (isMobile) {
			return {
				slidesToShow: 1,
				slidesToScroll: 1
			};
		}

		const baseProps = isTallVerticalCoupon
			? tallVerticalCouponCaroProps
			: couponCaroProps;

		// Ensure slidesToShow never exceeds the number of available promos
		const maxSlides = promos.length;
		const adjustedProps = {
			...baseProps,
			slidesToShow: Math.min(baseProps.slidesToShow, maxSlides),
			slidesToScroll: Math.min(baseProps.slidesToScroll, maxSlides)
		};

		// Also adjust responsive breakpoint settings
		if (adjustedProps.responsive) {
			adjustedProps.responsive = adjustedProps.responsive.map(
				(breakpoint) => ({
					...breakpoint,
					settings: {
						...breakpoint.settings,
						slidesToShow: Math.min(
							breakpoint.settings.slidesToShow,
							maxSlides
						),
						slidesToScroll: Math.min(
							breakpoint.settings.slidesToScroll,
							maxSlides
						)
					}
				})
			);
		}

		return adjustedProps;
	};

	const caroProps = getCarouselDisplayProps(
		isCouponCaro,
		isMobile,
		isTallVerticalCoupon,
		width
	);

	// Use media query setting for reduced motion (selectable in OS)
	const reducedMotionMatch =
		typeof window !== 'undefined' &&
		window.matchMedia('(prefers-reduced-motion: reduce)');

	// The following settings are enforced for accessibility
	const accessibilityOverrides = {
		accessibility: true,
		draggable: false, // Desktop only
		pauseOnFocus: false,
		pauseOnHover: false,
		fade: reducedMotionMatch && reducedMotionMatch.matches,
		infinite: infiniteFlag
	};

	const setAriaLive = useCallback(
		(disable) => {
			// Add carousel ARIA attributes to the elements dynamically created by Slick
			// ARIA specifications for carousels: https://www.w3.org/TR/wai-aria-practices/#carousel
			if (sliderRef && sliderRef.current) {
				const slideContainer =
					sliderRef.current.innerSlider.list.querySelector(
						'.slick-track'
					);

				if (slideContainer) {
					// Add aria attributes to the parent element of the slides
					slideContainer.setAttribute('aria-atomic', 'true');
					slideContainer.setAttribute(
						'aria-live',
						autoplaySlides || disable ? 'off' : 'polite'
					);
				}
			}
		},
		[autoplaySlides]
	);

	const setTabIndexForActiveSlide = () => {
		if (!sliderRef || !sliderRef.current) {
			return;
		}
		const slideElement =
			sliderRef.current.innerSlider.list.querySelector('.slick-active');
		if (slideElement) {
			slideElement.setAttribute('tabIndex', '1');
		}
	};

	useEffect(() => {
		if (isMobile) {
			const handleDragStart = (e) => {
				// Touch or mouse's primary button
				if (e.type !== 'mousedown' || e.button === 0) {
					setAutoplaySlides(false);
				}
			};
			const slideContainer =
				sliderRef.current.innerSlider.list.querySelector(
					'.slick-track'
				);

			slideContainer.addEventListener(
				'mousedown',
				handleDragStart,
				false
			);
			slideContainer.addEventListener(
				'touchstart',
				handleDragStart,
				false
			);

			return () => {
				slideContainer.removeEventListener(
					'mousedown',
					handleDragStart,
					false
				);
				slideContainer.removeEventListener(
					'touchstart',
					handleDragStart,
					false
				);
			};
		}
		return () => {};
	}, [sliderRef, isMobile]);

	// Once user interacts with slide controls, stop autoplay
	const onFocus = () => {
		setAutoplaySlides(false);
	};

	const trackInteraction = (swipe) => {
		let element;
		let result;
		const { oemId, title } = promos[visibleSlideIndex];
		if (swipe) {
			element =
				swipe === 'left'
					? 'Carousel next slide button'
					: 'Carousel previous button';
			result =
				swipe === 'left'
					? `Moved to carousel item ${visibleSlideIndex}`
					: `Move to previous photo ${visibleSlideIndex}`;
		} else {
			element = autoplaySlides
				? 'Carousel pause button'
				: 'Carousel play button';
			result = autoplaySlides ? 'Carousel paused' : 'Carousel played';
		}

		trackEvent({
			widgetName,
			windowId,
			element,
			result,
			dataToExcludeFromWidgetUserEvent: {
				title,
				oemId,
				index: visibleSlideIndex,
				count: promos.length,
				enableCarousel: true
			}
		});
	};

	const onSwipe = (swipeDirection) => {
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		// This event happens after slide has been pushed into place, but before
		//  the beforeChange() callback.
		setAutoplaySlides(false);
		trackInteraction(swipeDirection);
	};

	const onNext = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		setAutoplaySlides(false);
		sliderRef.current.slickNext();
		trackInteraction('left');
	};

	const onPrev = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		setAutoplaySlides(false);
		sliderRef.current.slickPrev();
		trackInteraction('right');
	};

	const onPlayPause = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		setAutoplaySlides(!autoplaySlides);
		trackInteraction();
	};

	// Load slide content for current index, two slides ahead, and one slide behind
	const loadPromoContents = (slideIndex) => {
		const oneAheadIndex = (visibleSlideIndex + 1) % numSlides;
		const twoAheadIndex = (visibleSlideIndex + 2) % numSlides;
		const oneBehindIndex = (visibleSlideIndex - 1 + numSlides) % numSlides;

		if (
			[
				visibleSlideIndex,
				oneAheadIndex,
				isCouponCaro ? twoAheadIndex : oneBehindIndex
			].includes(slideIndex) ||
			isCouponCaro
		) {
			setIsSlideVisible(true);
		}
	};

	return (
		<section
			aria-live="polite"
			aria-roledescription="carousel"
			aria-label={labels.get('SPECIAL_OFFERS')}
			data-testid={isCouponCaro ? 'coupon-carousel' : 'regular-carousel'}
		>
			<Slider
				{...slickDefaults}
				{...getCarouselProps()}
				{...accessibilityOverrides}
				autoplay={autoplaySlides && !isCouponCaro}
				autoplaySpeed={parseInt(autoplaySpeed, 10) || 5000}
				afterChange={(slideIndex) => {
					if (viewedSlides.indexOf(slideIndex) === -1) {
						viewedSlides.push(slideIndex);
						setViewedSlides(viewedSlides);
						setTabIndexForActiveSlide();
					}
				}}
				beforeChange={(_currentSlide, _nextSlide) => {
					setVisibleSlideIndex(_nextSlide);
					setPromoOutsiderButton(null);
				}}
				className="carousel hide-buttons"
				onInit={() => {
					setTabIndexForActiveSlide();
					loadPromoContents(0);
				}}
				onReInit={() => {
					setTabIndexForActiveSlide();
					setAriaLive();
				}}
				onSwipe={(swipe) => onSwipe(swipe)}
				appendDots={(dots) =>
					CarouselControls({
						dots,
						visibleSlideIndex,
						belowPosition:
							isMobile ||
							aspectRatio === ASPECT_RATIO.DESKTOP_10X1 ||
							isCouponCaro,
						onPlayPause,
						onPrev,
						onNext,
						onFocus,
						autoplaySlides,
						aspectRatio,
						isMobile,
						labels,
						caroProps,
						promosLength: promos.length,
						isCouponCaro
					})
				}
				ref={sliderRef}
			>
				{promos.map((promo, i) => (
					<Promo
						key={promo.id}
						isMobile={isMobile}
						isCurrent={visibleSlideIndex === i}
						promoIndex={i}
						isVisible={isSlideVisible}
						promo={promo}
						positionLabel={`${i + 1} ${labels.get('OF')} ${
							promos.length
						}`}
						role="group"
						roleDescription="slide"
					/>
				))}
			</Slider>
		</section>
	);
};

Carousel.propTypes = {
	isMobile: PropTypes.bool,
	promos: PropTypes.arrayOf(
		PropTypes.shape({
			type: PropTypes.string.isRequired,
			id: PropTypes.string.isRequired,
			autoGenerated: PropTypes.bool.isRequired,
			customAssets: PropTypes.shape({}),
			oemId: PropTypes.string,
			title: PropTypes.string
		})
	).isRequired,
	slickProps: PropTypes.shape({}),
	width: PropTypes.number,
	isCouponCaro: PropTypes.bool
};

export default Carousel;
